<script setup lang="ts">
import type { ProfileUpdateFormApi } from "@/components/Account/types.ts";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import type { FormApi } from "@tanstack/vue-form";
import { toRefs } from "vue";

interface Props {
  isEditing: boolean;
  formTitle: string;
  editable: boolean;
  parentForm: ProfileUpdateFormApi;
}

const props = withDefaults(defineProps<Props>(), {
  isEditing: false,
  formTitle: "",
  editable: true,
});
const { parentForm } = toRefs(props);

const emit = defineEmits(["editForm", "cancelEditForm", "saveForm"]);
const clickSaving = () => {
  // isSaving.value = true;
  emit("saveForm");
};
</script>

<template>
  <div class="w-full p-6 space-y-8">
    <div class="flex justify-between">
      <h1 class="uppercase font-bold">{{ formTitle }}</h1>
      <div v-if="editable">
        <div v-if="!isEditing">
          <BaseButton
            type="button"
            class="flex mr-0 text-base items-center"
            @click.prevent="emit('editForm')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-5 h-5"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125"
              />
            </svg>
            <span class="uppercase ml-2 text-base font-semibold">edit</span>
          </BaseButton>
        </div>
        <div v-else class="flex space-x-2">
          <BaseButton
            type="button"
            class="flex mr-0 text-base items-center"
            outlined
            @click.prevent="emit('cancelEditForm')"
          >
            <span class="uppercase text-base font-semibold">cancel</span>
          </BaseButton>
          <parentForm.Subscribe>
            <template v-slot="{ canSubmit, isSubmitting }">
              <BaseButton
                type="button"
                class="flex mr-0 text-base items-center"
                :disabled="!canSubmit || isSubmitting"
                @click.prevent="clickSaving"
              >
                <span class="uppercase text-base font-semibold">save</span>
              </BaseButton>
            </template>
          </parentForm.Subscribe>
        </div>
      </div>
    </div>
    <div class="inset-0 my-6 flex items-center" aria-hidden="true">
      <div class="w-full border-t border-gray-300" />
    </div>
    <div class="flex space-y-4 flex-col w-full my-4">
      <slot />
    </div>
  </div>
</template>
